import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { lifecycle } from './lifecycle';
import { keyboard } from './keyboard';
import { network } from './network';
import { dispatchBack, openExternal, subscribeAppUrl, subscribeNativeBack } from './navigation';
import { hideLaunchSplash, initializeSystemUi } from './systemUi';
import { subscribeViewport } from './viewport';
import { emitNotificationTapStage, initializePushNotificationActions, initializePushNotifications } from './pushNotifications';
import { useAuth } from '../context/AuthContext';

function publish(name, detail) {
  try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch {}
}

export default function PlatformRuntime() {
  const { user, isAuthReady } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const routeStack = useRef([location.pathname]);
  const locationRef = useRef(location.pathname);
  const pendingNotificationRouteRef = useRef(null);
  const authReadyRef = useRef(false);
  const navigateRef = useRef(navigate);
  authReadyRef.current = Boolean(isAuthReady && user);
  navigateRef.current = navigate;

  const handleNotificationRoute = useCallback((route) => {
    if (!authReadyRef.current) {
      pendingNotificationRouteRef.current = route;
      return;
    }
    navigateRef.current(route);
    emitNotificationTapStage('navigation_requested');
  }, []);

  useEffect(() => {
    const next = location.pathname;
    if (next !== locationRef.current) {
      routeStack.current.push(next);
      if (routeStack.current.length > 50) routeStack.current.shift();
      locationRef.current = next;
    }
    if (next.startsWith('/chat/')) emitNotificationTapStage('chat_route_rendered');
  }, [location.pathname]);

  useEffect(() => {
    initializeSystemUi().catch(() => {});
    const frame = requestAnimationFrame(() => { hideLaunchSplash().catch(() => {}); });
    const stopViewport = subscribeViewport();
    const stopLifecycle = lifecycle.subscribe((event) => publish('zodiacus:lifecycle', event));
    const stopKeyboard = keyboard.subscribe((state) => publish('zodiacus:keyboard', state));
    const stopNetwork = network.subscribe((state) => publish('zodiacus:network', state));
    return () => {
      cancelAnimationFrame(frame);
      stopViewport(); stopLifecycle(); stopKeyboard(); stopNetwork();
    };
  }, []);

  useEffect(() => subscribeNativeBack(async () => {
    const result = await dispatchBack({
      pathname: locationRef.current,
      stackDepth: routeStack.current.length,
      navigate: (to, options) => {
        if (to === -1) routeStack.current.pop();
        navigate(to, options);
      },
    });
    return result;
  }), [navigate]);

  useEffect(() => subscribeAppUrl((classified) => {
    if (classified.kind === 'internal') navigate(classified.path);
    else if (classified.kind === 'external') openExternal(classified.url).catch(() => {});
  }), [navigate]);

  useEffect(() => {
    let stop = () => {};
    let disposed = false;
    // A native cold route is one-shot. Defer its destructive consume past a
    // possible development StrictMode setup/cleanup probe so the stable mount
    // owns the callback and its pending-route ref.
    const timer = setTimeout(() => {
      if (disposed) return;
      initializePushNotificationActions({ onRoute: handleNotificationRoute, onTapStage: emitNotificationTapStage })
        .then((cleanup) => { if (disposed) cleanup(); else stop = cleanup; })
        .catch(() => {});
    }, 0);
    return () => { disposed = true; clearTimeout(timer); stop(); };
  }, [handleNotificationRoute]);

  useEffect(() => {
    if (isAuthReady && !user) pendingNotificationRouteRef.current = null;
    if (!isAuthReady || !user) return undefined;
    emitNotificationTapStage('auth_ready');
    const pendingRoute = pendingNotificationRouteRef.current;
    if (pendingRoute) {
      pendingNotificationRouteRef.current = null;
      // The public landing page redirects an authenticated cold launch to
      // /home in a passive effect. Queue the validated notification route
      // behind that startup redirect so it is the deterministic final route.
      const timer = setTimeout(() => {
        navigate(pendingRoute);
        emitNotificationTapStage('navigation_requested');
      }, 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [navigate, isAuthReady, user]);

  useEffect(() => {
    if (!isAuthReady || !user) return undefined;
    let stop = () => {};
    let disposed = false;
    // Defer native listener setup past React StrictMode's development-style
    // setup/cleanup/setup probe. Otherwise the first async initializer owns the
    // global guard and its disposed continuation removes the live listener.
    const timer = setTimeout(() => {
      if (disposed) return;
      initializePushNotifications({ navigate, onTapStage: emitNotificationTapStage }).then((cleanup) => { if (disposed) cleanup(); else stop = cleanup; }).catch(() => {});
    }, 0);
    return () => { disposed = true; clearTimeout(timer); stop(); };
  }, [navigate, isAuthReady, user]);

  return null;
}
